import {
  getNewBundleId,
  getNewPageId,
} from '@/modules/presentation/clientPresentation/ClientPresentationDesigner.utils';
import {
  buildWaterfallPageItems,
  WaterfallPageItem,
} from '@/modules/presentation/components/WaterfallPageReorderList';
import { PresentationBundleKind, PresentationPageKind } from '@/types/schema';

import { getCarriedOverBundleFields } from '../../../PresentationTemplateEditor.bundleFields';
import {
  PresentationTemplateEditorBundle,
  PresentationTemplateEditorPage,
} from '../../../PresentationTemplateEditor.types';

export type { WaterfallPageItem };

type WaterfallComparisonConfigKey =
  | 'includeCoverPage'
  | 'includeAssumptionsPage';

/** The 3 waterfall comparison page types in their default order. */
export const WATERFALL_COMPARISON_PAGE_DEFINITIONS = [
  {
    id: 'cover',
    pageKind: PresentationPageKind.WaterfallComparisonCoverPage,
    configKey: 'includeCoverPage' as const,
    label: 'Cover',
  },
  {
    id: 'assumptions',
    pageKind: PresentationPageKind.WaterfallComparisonAssumptionsPage,
    configKey: 'includeAssumptionsPage' as const,
    label: 'Assumptions',
  },
  {
    id: 'comparison',
    pageKind: PresentationPageKind.WaterfallComparisonPage,
    configKey: null,
    label: 'Comparison',
    alwaysIncluded: true,
  },
];

export interface WaterfallComparisonBundleConfigFormShape {
  pages: WaterfallPageItem[];
  templateNotes: string;
}

/**
 * Returns the default page items for the waterfall comparison config modal.
 * In create mode (no bundle), all pages are checked.
 * In edit mode, restores checked state from the bundle's config and page order.
 */
export function getDefaultWaterfallComparisonPages(
  bundle?: PresentationTemplateEditorBundle
): WaterfallPageItem[] {
  if (!bundle) {
    // Create mode: all checked, default order
    return WATERFALL_COMPARISON_PAGE_DEFINITIONS.map((def) => ({
      ...def,
      checked: true,
    }));
  }

  const config = bundle.waterfallComparisonTemplateBundleConfiguration;
  const savedPageKinds = bundle.pages.map((p) => p.kind);

  // When config is missing, derive flags from the saved pages array so we
  // don't silently deselect previously-included pages.
  const effectiveConfig = config ?? {
    includeCoverPage: savedPageKinds.includes(
      PresentationPageKind.WaterfallComparisonCoverPage
    ),
    includeAssumptionsPage: savedPageKinds.includes(
      PresentationPageKind.WaterfallComparisonAssumptionsPage
    ),
  };

  return buildWaterfallPageItems(
    effectiveConfig,
    savedPageKinds,
    WATERFALL_COMPARISON_PAGE_DEFINITIONS
  );
}

/**
 * Builds a PresentationTemplateEditorBundle from the modal's form values.
 * In edit mode, preserves the existing page objects (which may carry
 * page-level data like coverPage content) by matching on kind.
 *
 * Bundle-level fields the modal doesn't own come across via
 * `getCarriedOverBundleFields`. `templateNotes` is the exception — this modal
 * edits it, so the form value wins.
 */
export function buildWaterfallComparisonBundleFromForm(
  pages: WaterfallPageItem[],
  templateNotes: string,
  existingBundle?: PresentationTemplateEditorBundle
): PresentationTemplateEditorBundle {
  const checkedPages = pages.filter((p) => p.checked);

  // Index existing pages by kind so we can preserve page-level data
  const existingByKind = new Map<
    PresentationPageKind,
    PresentationTemplateEditorPage
  >();
  for (const page of existingBundle?.pages ?? []) {
    existingByKind.set(page.kind, page);
  }

  const bundlePages: PresentationTemplateEditorPage[] = checkedPages.map(
    (item) =>
      existingByKind.get(item.pageKind) ?? {
        id: getNewPageId(item.pageKind),
        kind: item.pageKind,
      }
  );

  // Build the config flags from checked state (only for toggleable pages)
  const configFlags: Record<WaterfallComparisonConfigKey, boolean> = {
    includeCoverPage: false,
    includeAssumptionsPage: false,
  };

  for (const page of pages) {
    if (page.configKey) {
      configFlags[page.configKey as WaterfallComparisonConfigKey] =
        page.checked;
    }
  }

  return {
    ...getCarriedOverBundleFields(existingBundle),
    id:
      existingBundle?.id ??
      getNewBundleId(PresentationBundleKind.WaterfallComparisonBundle),
    kind: PresentationBundleKind.WaterfallComparisonBundle,
    // This modal owns the field, so the form value overrides the carried one.
    templateNotes,
    waterfallComparisonTemplateBundleConfiguration: {
      __typename: 'WaterfallComparisonTemplateBundleConfiguration' as const,
      id: '',
      ...configFlags,
    },
    pages: bundlePages,
  };
}
